
import React, { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { type Location } from "@/utils/mapUtils";
import SearchInput from "./search/SearchInput";
import SearchActions from "./search/SearchActions";
import { handleLocationSearch } from "./search/searchUtils";

interface LocationSearchProps {
  onLocationFound: (location: Location) => void;
  onUseCurrentLocation: () => void;
  onToggleFallbackMap?: () => void;
  useFallbackMap?: boolean;
}

const LocationSearch: React.FC<LocationSearchProps> = ({ 
  onLocationFound, 
  onUseCurrentLocation,
  onToggleFallbackMap,
  useFallbackMap = false
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const { toast } = useToast();
  
  // Abort search if component unmounts during an active search
  useEffect(() => {
    return () => {
      if (isSearching) {
        setIsSearching(false);
      }
    };
  }, [isSearching]);

  const handleSearch = () => {
    if (isSearching) return; // Prevent multiple simultaneous searches
    
    if (!searchInput.trim()) {
      toast({
        title: "Empty Search",
        description: "Please enter a location to search for",
        variant: "destructive",
      });
      return;
    }
    
    console.log("Starting search for:", searchInput);
    handleLocationSearch(
      searchInput,
      setIsSearching,
      onLocationFound,
      () => setSearchInput("")
    );
  };

  return (
    <div className="p-4 flex space-x-2">
      <SearchInput
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onSearch={handleSearch}
        isSearching={isSearching}
      />
      
      <SearchActions
        onUseCurrentLocation={onUseCurrentLocation}
        onToggleFallbackMap={onToggleFallbackMap}
        useFallbackMap={useFallbackMap}
        isSearching={isSearching}
      />
    </div>
  );
};

export default LocationSearch;
